import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ToastService } from '../../../services/toast.service';
import { Subscription, of, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import {
  VendorAdapterInfo,
  VendorAgentJob,
  VendorAgentSummary,
  VendorCredentialInput,
  VendorCredentialStatus,
  VendorCredentialTestResult,
  VendorPlateMatching,
  VendorSyncJobProgress,
  VendorSyncService,
} from '../../services/vendor-sync.service';
import {
  AdminLocationRow,
  LocationSettingsService,
} from '../../services/location-settings.service';
import {
  VendorPlanMapping,
  VendorPlanMappingService,
} from '../../services/vendor-plan-mapping.service';
import { VendorModeService } from '../../../services/vendor-mode.service';
import {
  MAX_SYNC_LIMIT,
  VendorSystem,
  invalidMappingCount,
  isNightlySyncOn,
  isScheduledSync,
  parseSyncLimit,
} from './vendor-migration.model';

/** One past run, flattened into the columns the history table renders. */
export interface SyncHistoryRow {
  id: string;
  /** "Sep 15, 11:32" — day plus time, because manual syncs repeat within a day. */
  when: string;
  status: string;
  tone: 'ok' | 'danger' | 'neutral';
  /**
   * Counts for a run that settled successfully. Null for a failed or in-flight
   * run, where `note` takes the place of the count columns rather than printing
   * zeroes that read as "imported nobody" instead of "never got that far".
   */
  counts: { processed: number; inserted: number; updated: number } | null;
  /** Failure reason, or progress text, shown across the count columns. */
  note: string | null;
  /**
   * The CSV this run handed to the import, as a signed download link. Null when
   * the run never uploaded one (it failed before the fetch finished) or the
   * file has since been removed from storage.
   */
  csvUrl: string | null;
  /** File name to save the CSV under; the vendor and run time are in it. */
  csvName: string;
}

/** Runs still in flight have no meaningful counts to show yet. */
const SETTLED_HISTORY_STATUSES = new Set(['completed', 'failed']);

/** Human-readable text for the reason codes vendorPlanMapping.service.js returns. */
const PLAN_MAPPING_INVALID_REASONS: Record<string, string> = {
  package_not_found: 'Package no longer exists',
  wrong_tenant: 'Package belongs to a different car wash',
  package_deleted: 'Package has been deleted',
  package_inactive: 'Package is inactive',
  not_car_wash_package: 'Only car wash packages can be mapped',
  not_monthly_membership: 'Only monthly membership packages can be mapped',
  period_day_not_supported: 'Daily packages cannot back a membership',
  monthly_with_zero_frequency_silent_deny: 'Monthly package has no wash allowance set',
  unrecognized_wash_count_period: 'Package configuration is not recognized',
};

/** BullMQ states that mean a vendor sync job has stopped. */
const TERMINAL_STATES = new Set(['completed', 'failed']);

/** `vendor_sync_agent_jobs.status` values that mean the agent job has stopped. */
const TERMINAL_AGENT_STATUSES = new Set(['completed', 'failed', 'expired']);

/**
 * Agent job status → the BullMQ-shaped `jobState` the progress panel renders.
 *
 * Mapping rather than rendering the raw status keeps one progress surface for
 * both executors: `isJobRunning`, `percent`, `skippedCount` and the pill all
 * keep working untouched. `expired` maps to failed because that is what it is —
 * a job no agent ever claimed inside its 24h window — and the row's
 * `error_message` says so.
 */
const AGENT_STATUS_TO_JOB_STATE: Record<string, string> = {
  pending: 'waiting',
  claimed: 'active',
  running: 'active',
  completed: 'completed',
  failed: 'failed',
  expired: 'failed',
};

const JOB_POLL_INTERVAL_MS = 2500;

interface PlanMappingRow {
  planName: string;
  packageId: string;
  valid: boolean;
  reason?: string;
}

/**
 * One vendor bridge, start to finish.
 *
 * Everything about a single system lives here — sign-in, plan mapping, a manual
 * sync and its progress, pausing, and the cut-over — rather than being spread
 * across a step labelled "Provide data". The important addition over what
 * shipped before is the cut-over: turning a vendor off used to read as an
 * unexplained pause, and the only way to withdraw a car wash was to delete its
 * credentials with no acknowledgement that this is the *intended* end of a
 * migration. Same API call; it is now framed, evidenced and gated.
 */
@Component({
  selector: 'app-vendor-system',
  standalone: true,
  // This app keeps Zone + Eager (see eslint.config.js): Angular 22 defaults new
  // components to OnPush, and under OnPush these panels load their data but never
  // repaint, leaving the tab stuck on "Loading..." forever.
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, MatSlideToggleModule],
  templateUrl: './vendor-system.component.html',
  styleUrls: ['./vendor-system.component.scss'],
})
export class VendorSystemComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) companyId!: string;
  @Input({ required: true }) adapter!: VendorAdapterInfo;
  /** Derived state from the list, or null for a system being added for the first time. */
  @Input() system: VendorSystem | null = null;
  @Input() companyName = '';
  @Output() back = new EventEmitter<void>();

  credentialStatus: VendorCredentialStatus | null = null;
  credentialStatusLoading = false;
  credentialValues: Record<string, string> = {};
  showCredentialForm = false;
  credentialTestResult: VendorCredentialTestResult | null = null;
  credentialError = '';
  isTestingCredentials = false;
  isSavingCredentials = false;
  isTogglingSyncEnabled = false;
  isTogglingNightlySync = false;

  planMapping: VendorPlanMapping | null = null;
  planMappingRows: PlanMappingRow[] = [];
  planMappingLoading = false;
  planMappingError = '';
  planMappingSaved = false;
  isSavingPlanMapping = false;
  newPlanName = '';
  newPackageId = '';

  jobId: string | null = null;
  jobState: string | null = null;
  jobProgress: VendorSyncJobProgress | null = null;
  jobFailedReason: string | null = null;
  isStartingSync = false;
  /** Optional "Row limit" input; blank is a full sync. */
  syncLimit: number | string | null = null;
  /** The limit the sync on screen was started with, for the progress panel. */
  runningSyncLimit: number | null = null;
  /** The number input holds text the browser cannot parse; see parseSyncLimit. */
  syncLimitBadInput = false;
  readonly maxSyncLimit = MAX_SYNC_LIMIT;

  // ── Lane redeem controls (meta.plateMatching, CWS-920) ────────────────────
  shopLocations: AdminLocationRow[] = [];
  shopLocationsLoading = false;
  isSavingPlateMatching = false;
  plateMatchingError = '';

  /** Pull executor: server (cloud) vs agent (agent machine). */
  isSavingExecutor = false;
  executorError = '';
  agents: VendorAgentSummary[] = [];
  agentJob: VendorAgentJob | null = null;
  /** The delegated job this panel is currently following, if any. */
  agentJobId: string | null = null;

  /** Cut-over confirmation. Typed name must match the car wash, not the vendor. */
  showCutOver = false;
  cutOverConfirmation = '';
  isCuttingOver = false;

  private readonly vendorSyncService = inject(VendorSyncService);
  private readonly planMappingService = inject(VendorPlanMappingService);
  private readonly vendorModeService = inject(VendorModeService);
  private readonly locationSettingsService = inject(LocationSettingsService);
  private readonly messageService = inject(ToastService);
  private credentialSub: Subscription | null = null;
  private planMappingSub: Subscription | null = null;
  private pollSub: Subscription | null = null;
  private agentPollSub: Subscription | null = null;
  private shopLocationsSub: Subscription | null = null;

  ngOnInit(): void {
    this.loadCredentialStatus();
    this.loadPlanMapping();
    this.loadShopLocations();
    this.loadAgents();
    this.loadLatestAgentJob();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['adapter'] && !changes['adapter'].firstChange) {
      this.resetForAdapterChange();
      this.loadCredentialStatus();
      this.loadPlanMapping();
    }
  }

  ngOnDestroy(): void {
    this.credentialSub?.unsubscribe();
    this.planMappingSub?.unsubscribe();
    this.stopPolling();
    this.stopShopLocationsLoad();
  }

  get label(): string {
    return this.adapter.displayName || this.adapter.name;
  }

  get isConnected(): boolean {
    return this.credentialStatus?.configured === true;
  }

  get isPaused(): boolean {
    return this.isConnected && this.credentialStatus?.enabled === false;
  }

  /** Credentials can only be stored when the encryption key is usable. */
  get canEditCredentials(): boolean {
    return this.credentialStatus?.encryptionConfigured !== false;
  }

  get credentialFields(): VendorAdapterInfo['credentialFields'] {
    return this.adapter.credentialFields ?? [];
  }

  /** Every field the adapter marks required must be filled to test or save. */
  get hasCredentialInput(): boolean {
    const required = this.credentialFields.filter(f => f.required);
    if (!required.length) return false;
    return required.every(f => (this.credentialValues[f.key] ?? '').trim().length > 0);
  }

  get isJobRunning(): boolean {
    // An agent job picked back up on reload has no BullMQ id, only agentJobId.
    const tracking = this.jobId || this.agentJobId;
    return Boolean(tracking && this.jobState && !TERMINAL_STATES.has(this.jobState));
  }

  /** Tone of the sync job pill: green once it finished, red if it failed, blue otherwise. */
  get jobStateTone(): 'ok' | 'danger' | 'info' {
    if (this.jobState === 'failed') return 'danger';
    if (this.jobState === 'completed') return 'ok';
    return 'info';
  }

  /**
   * A sync can only start once credentials are stored — they are resolved
   * server-side, so without them the request fails after the operator has
   * already committed. A paused bridge is rejected server-side too.
   */
  get canStartSync(): boolean {
    return (
      this.isConnected &&
      !this.isPaused &&
      !this.isJobRunning &&
      !this.isStartingSync &&
      this.isSyncLimitValid
    );
  }

  /** Whether the 05:30 PT sweep includes this vendor. Absent from the status means on. */
  get isNightlySyncOn(): boolean {
    return isNightlySyncOn(this.credentialStatus);
  }

  /** Nightly turned off on a live sync: worth a standing warning, not just a toggle state. */
  get isManualOnly(): boolean {
    return this.isConnected && !this.isPaused && !this.isNightlySyncOn;
  }

  /** The sync on screen is the nightly sweep's, not one started from this panel. */
  get isNightlySync(): boolean {
    return isScheduledSync(this.agentJob);
  }

  get isSyncLimitValid(): boolean {
    return parseSyncLimit(this.syncLimit, this.syncLimitBadInput).valid;
  }

  /**
   * The limit of the sync on screen: the one just started here, or — for a
   * delegated sync picked back up after a reload — the one stored on its job.
   */
  get displayedSyncLimit(): number | null {
    if (this.runningSyncLimit) return this.runningSyncLimit;
    return this.agentJobId ? (this.agentJob?.options?.limit ?? null) : null;
  }

  onSyncLimitInput(event: Event): void {
    this.syncLimitBadInput = Boolean((event.target as HTMLInputElement | null)?.validity?.badInput);
  }

  get invalidMappings(): number {
    return invalidMappingCount(this.planMapping);
  }

  /** Members fetched but not handed to the importer, because they could not be matched. */
  get skippedCount(): number {
    return this.jobProgress?.skipped ?? 0;
  }

  get reportTotal(): number | null {
    return this.jobProgress?.reportTotal ?? null;
  }

  /**
   * True while the first batch is still being fetched, so zeros mean "waiting",
   * not "nothing found".
   */
  get isAwaitingFirstPage(): boolean {
    return this.isJobRunning && (this.jobProgress?.fetched ?? 0) === 0;
  }

  /**
   * A delegated sync sitting `pending`: enqueued, but no agent has claimed it.
   *
   * Worth its own message rather than folding into isAwaitingFirstPage, which
   * blames the vendor for being slow. Here nothing is talking to the vendor at
   * all, and the usual cause is that the laptop agent is not running — which
   * the operator can see and fix, but only if we say so.
   */
  get isAwaitingAgentPickup(): boolean {
    return this.agentJob?.status === 'pending';
  }

  /**
   * Whether to show the progress panel. Not `jobId` alone: a delegated sync
   * resumed after a page reload has an agent job to follow and no BullMQ id.
   */
  get showProgressPanel(): boolean {
    return Boolean(this.jobId || this.agentJobId);
  }

  /**
   * Percentage only once the vendor has told us its total. Before that there is
   * no honest denominator, and a bar that invents one misrepresents progress.
   */
  get percent(): number | null {
    const total = this.reportTotal;
    const fetched = this.jobProgress?.fetched ?? 0;
    if (!total || total <= 0) return null;
    return Math.max(0, Math.min(100, Math.round((fetched / total) * 100)));
  }

  get cutOverReady(): boolean {
    return this.system?.state === 'ready_to_cut_over';
  }

  /**
   * Guard on the car wash name rather than the vendor's: a super-admin works
   * across companies, and cutting over the right vendor at the wrong car wash is
   * the mistake worth making expensive.
   */
  get cutOverConfirmed(): boolean {
    const expected = (this.companyName || this.label).trim().toLowerCase();
    return this.cutOverConfirmation.trim().toLowerCase() === expected && expected.length > 0;
  }

  get cutOverExpectedText(): string {
    return this.companyName || this.label;
  }

  /**
   * Resolved redeem controls, or the server-side defaults while the status is still
   * loading or when talking to a svc-vendor-sync that predates the field.
   *
   * Must mirror libs/core vendorPlateMatching: auto-redeem is opt-in (off until a
   * super admin turns it on), the location allowlist is opt-out (empty = every
   * location). Defaulting autoRedeem to true here would show a shop as
   * auto-redeeming when the gate is not.
   *
   * collectPayment is opt-in for the same reason and is deliberately not editable
   * from this tab — it gates Collect payment / card-on-file in the Active
   * subscription modal, which owns that control. It appears here only so the
   * fallback is a complete VendorPlateMatching; every write from this component
   * goes out as a Partial, so an unrendered field is never clobbered.
   */
  get plateMatching(): VendorPlateMatching {
    return (
      this.credentialStatus?.plateMatching ?? {
        orderFormRedeem: false,
        autoRedeem: false,
        collectPayment: false,
        locationIds: [],
      }
    );
  }

  /** Empty allowlist means every location, which is the pre-feature behaviour. */
  get isPlateMatchingLocationRestricted(): boolean {
    return this.plateMatching.locationIds.length > 0;
  }

  isPlateMatchingLocationSelected(locationId: string): boolean {
    return this.plateMatching.locationIds.includes(String(locationId).toLowerCase());
  }

  /** Branch names for the current allowlist, for a one-line summary. */
  get selectedPlateMatchingLocationNames(): string[] {
    const selected = new Set(this.plateMatching.locationIds);
    return this.shopLocations
      .filter(l => selected.has(String(l.id).toLowerCase()))
      .map(l => l.branch || l.id);
  }

  /**
   * Plain-language statement of what the gate will do, shown under the controls.
   *
   * The two controls answer different questions and read confusingly together, so
   * the summary states the outcome rather than echoing the flags.
   */
  /**
   * Whether the connection test reported a member count at all.
   *
   * Deliberately not truthiness: a site reporting **0 members** is the strongest
   * signal that these credentials point at the wrong site or an empty location,
   * and it is exactly what the sentence this guards exists to surface. `@if
   * (credentialTestResult?.memberCount)` hid that case, because 0 is falsy.
   *
   * Lives here rather than in the template because the template cannot express it
   * without `!= null` — which the app's eqeqeq rule rejects — and `!== undefined`
   * alone would trust the declared type over what JSON can actually deliver. The
   * field is typed `memberCount?: number`, but it is parsed from a vendor
   * response, so an explicit null is possible and must count as "not reported".
   */
  get hasTestMemberCount(): boolean {
    const count = this.credentialTestResult?.memberCount;
    return count !== undefined && count !== null;
  }

  /**
   * What the order form does now. Separate from plateMatchingSummary because that one
   * describes the gate — reusing it after an order-form save answered a question the
   * operator had not asked, and read as confirmation of a change they did not make.
   */
  get orderFormRedeemSummary(): string {
    return this.plateMatching.orderFormRedeem
      ? `The order form offers Redeem Existing Membership, so an attendant can scan a ${this.label} member's card.`
      : `The order form no longer offers Redeem Existing Membership. Gate redemption and the membership itself are unaffected.`;
  }

  get plateMatchingSummary(): string {
    const { autoRedeem, locationIds } = this.plateMatching;
    const where =
      locationIds.length === 0
        ? 'at every location'
        : `at ${locationIds.length} of ${this.shopLocations.length || locationIds.length} locations`;
    return autoRedeem
      ? `A camera plate read redeems a ${this.label} membership automatically ${where}.`
      : `A camera plate read asks an attendant rather than redeeming automatically (${where}).`;
  }

  /**
   * Groups an operator has explicitly opened or closed. Absent means "use the
   * default". Settings are not in here: they are never collapsed, because they
   * are the reason most visits happen.
   */
  private readonly sectionOverrides: Record<string, boolean> = {};

  /**
   * The one group worth opening unasked, or null to leave everything closed.
   *
   * Deliberately keyed on something being *wrong* rather than on progress through
   * a sequence: this screen is not a wizard, and a healthy bridge should open
   * fully collapsed rather than pointing at a "current step" it does not have.
   * A bridge with no credentials cannot do anything; unmapped plans still import
   * members but leave them nothing to redeem; a bridge that looks finished wants
   * its ending surfaced. Anything else needs no attention, so nothing opens.
   */
  get sectionNeedingAttention(): 'connect' | 'plans' | 'finish' | null {
    if (!this.isConnected) return 'connect';
    if (!this.planMappingRows.length || this.invalidMappings > 0) return 'plans';
    if (this.cutOverReady) return 'finish';
    return null;
  }

  isSectionOpen(key: string): boolean {
    const override = this.sectionOverrides[key];
    return override === undefined ? key === this.sectionNeedingAttention : override;
  }

  toggleSection(key: string): void {
    this.sectionOverrides[key] = !this.isSectionOpen(key);
  }

  /**
   * One-line summaries for a collapsed section. Each states the fact an operator
   * would otherwise expand to find, so a closed section still answers "is this
   * step done?" without a click.
   */
  get connectSummary(): string {
    if (this.credentialStatusLoading) return 'Checking…';
    if (!this.isConnected) return 'Not connected';
    const code = this.credentialStatus?.locationCode;
    return code ? `Connected · ${code}` : 'Connected';
  }

  get plansSummary(): string {
    if (this.planMappingLoading) return 'Loading…';
    const total = this.planMappingRows.length;
    if (!total) return 'No plans mapped';
    const bad = this.invalidMappings;
    const plans = `${total} ${total === 1 ? 'plan' : 'plans'} mapped`;
    // Name the consequence, not the validation state: an unmapped plan still
    // imports its members, they just arrive with nothing to redeem.
    return bad > 0 ? `${plans}, ${bad} skipping members` : plans;
  }

  get rosterSummary(): string {
    if (this.isJobRunning) return 'Sync running';
    if (!this.isConnected) return 'Not connected';
    if (this.isPaused) return 'Sync paused';
    if (!this.isNightlySyncOn) return 'Manual only';
    return 'Nightly at 05:30 PT';
  }

  /**
   * Past runs for this vendor, newest first.
   *
   * Read from the derived system rather than fetched here: the list page already
   * pulls one page of import history to derive `lastSync`, and a second request
   * for the same rows would only invite the two disagreeing.
   */
  get syncHistory(): SyncHistoryRow[] {
    return (this.system?.history ?? []).map(item => {
      const settled = SETTLED_HISTORY_STATUSES.has(item.status);
      const failed = item.status === 'failed';
      const showCounts = settled && !failed;
      return {
        id: item.id,
        when: this.formatRunTime(item.created_at),
        status: item.status,
        tone: failed ? 'danger' : item.status === 'completed' ? 'ok' : 'neutral',
        counts: showCounts
          ? {
              processed: item.processed_count ?? 0,
              inserted: item.inserted_count ?? 0,
              updated: item.duplicate_count ?? 0,
            }
          : null,
        note: showCounts
          ? null
          : failed
            ? // A failed run's counts are whatever it reached before breaking, so
              // the reason is the only honest thing to show.
              item.error_message || 'No reason recorded'
            : 'Still running',
        csvUrl: item.source_file_url || null,
        csvName: item.source_file_name || 'vendor-sync.csv',
      };
    });
  }

  get hasSyncHistory(): boolean {
    return this.syncHistory.length > 0;
  }

  /**
   * Whether older runs may exist beyond the window the list page fetched. Shown
   * so an empty or short list is not read as "this is everything".
   */
  get isSyncHistoryTruncated(): boolean {
    return this.system?.historyTruncated === true;
  }

  private formatRunTime(value: string | undefined): string {
    if (!value) return 'Unknown time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown time';
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  get finishSummary(): string {
    if (this.cutOverReady) return 'Looks finished — ready to cut over';
    return this.system?.statusLabel ?? 'Bridging';
  }

  loadShopLocations(): void {
    // Drop any request still in flight for the previous company rather than
    // skipping this one: the caller has already cleared the list, so refusing to
    // reload would leave the picker permanently empty for the new company.
    this.stopShopLocationsLoad();
    if (!this.companyId) return;
    const companyId = this.companyId;
    this.shopLocationsLoading = true;
    this.shopLocationsSub = this.locationSettingsService.listLocations(companyId).subscribe({
      next: response => {
        if (companyId !== this.companyId) return;
        this.shopLocationsSub = null;
        this.shopLocationsLoading = false;
        this.shopLocations = Array.isArray(response.data) ? response.data : [];
      },
      error: () => {
        if (companyId !== this.companyId) return;
        this.shopLocationsSub = null;
        this.shopLocationsLoading = false;
        // Non-fatal: the toggle still works, the picker just cannot be rendered.
        this.shopLocations = [];
      },
    });
  }

  onToggleAutoRedeem(): void {
    this.savePlateMatching({ autoRedeem: !this.plateMatching.autoRedeem });
  }

  /**
   * CWS-1369. Writes an explicit boolean either way, which is the point: the stored value
   * is tri-state, and the first toggle is what turns "nobody has decided" into a decision.
   */
  onToggleOrderFormRedeem(): void {
    this.savePlateMatching({ orderFormRedeem: !this.plateMatching.orderFormRedeem });
  }

  onToggleRedeemLocation(locationId: string): void {
    const id = String(locationId).toLowerCase();
    const current = this.plateMatching.locationIds;
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    this.savePlateMatching({ locationIds: next });
  }

  /** Clear the restriction — back to "every location", the default. */
  onClearRedeemLocations(): void {
    if (!this.isPlateMatchingLocationRestricted) return;
    this.savePlateMatching({ locationIds: [] });
  }

  /**
   * Saves immediately rather than behind a Save button: each control is a single
   * decision, and the server returns the resolved state, so there is nothing to
   * batch and no draft worth losing.
   */
  private savePlateMatching(patch: Partial<VendorPlateMatching>): void {
    if (this.isSavingPlateMatching || !this.companyId) return;
    const companyId = this.companyId;
    const vendor = this.adapter.name;
    // Which control was touched, so the toast can describe that one rather than
    // defaulting to the gate summary for every save.
    const touchedOrderFormRedeem = Object.prototype.hasOwnProperty.call(patch, 'orderFormRedeem');
    this.isSavingPlateMatching = true;
    this.plateMatchingError = '';

    this.vendorSyncService.setPlateMatching(vendor, companyId, patch).subscribe({
      next: response => {
        // Always released, even for a response the guard below discards, or the
        // section would stay locked against further edits.
        this.isSavingPlateMatching = false;
        if (companyId !== this.companyId || vendor !== this.adapter.name) return;
        // Render from the server's resolved values — it, not this component, decides
        // what an empty allowlist or an absent flag means.
        this.credentialStatus = response.data ?? this.credentialStatus;
        this.messageService.add({
          severity: 'success',
          summary: touchedOrderFormRedeem ? 'Order form updated' : 'Redeem settings saved',
          detail: touchedOrderFormRedeem ? this.orderFormRedeemSummary : this.plateMatchingSummary,
        });
      },
      error: err => {
        this.isSavingPlateMatching = false;
        if (companyId !== this.companyId || vendor !== this.adapter.name) return;
        this.plateMatchingError = this.describeError(
          err,
          touchedOrderFormRedeem
            ? 'Failed to update the order form setting'
            : 'Failed to save redeem settings'
        );
      },
    });
  }

  private stopShopLocationsLoad(): void {
    this.shopLocationsSub?.unsubscribe();
    this.shopLocationsSub = null;
    this.shopLocationsLoading = false;
  }

  describePlanMappingReason(reason?: string): string {
    if (!reason) return 'This package is no longer valid';
    return PLAN_MAPPING_INVALID_REASONS[reason] ?? 'This package is no longer valid';
  }

  formatDate(value?: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  }

  loadCredentialStatus(): void {
    this.credentialSub?.unsubscribe();
    const companyId = this.companyId;
    const vendor = this.adapter.name;
    this.credentialStatusLoading = true;
    this.credentialError = '';
    this.credentialSub = this.vendorSyncService.getCredentialStatus(vendor, companyId).subscribe({
      next: response => {
        if (companyId !== this.companyId || vendor !== this.adapter.name) return;
        this.credentialStatusLoading = false;
        this.credentialStatus = response.data ?? null;
        // Not connected → the operator has to enter credentials, so open the
        // form for them. Connected → collapse it, so returning here does not
        // imply re-entry is required.
        this.showCredentialForm = !this.credentialStatus?.configured;
      },
      error: err => {
        if (companyId !== this.companyId || vendor !== this.adapter.name) return;
        this.credentialStatusLoading = false;
        this.credentialStatus = null;
        this.credentialError = this.describeError(err, `Failed to load ${this.label} status`);
      },
    });
  }

  loadPlanMapping(): void {
    this.planMappingSub?.unsubscribe();
    const companyId = this.companyId;
    const vendor = this.adapter.name;
    this.planMappingLoading = true;
    this.planMappingError = '';
    this.planMappingSaved = false;
    this.planMappingSub = this.planMappingService.getPlanMapping(companyId, vendor).subscribe({
      next: response => {
        if (companyId !== this.companyId || vendor !== this.adapter.name) return;
        this.planMappingLoading = false;
        this.planMapping = response.data ?? null;
        this.planMappingRows = (this.planMapping?.plan_package_map ?? []).map(entry => ({
          planName: entry.plan_name,
          packageId: entry.package_id,
          valid: entry.valid,
          reason: entry.reason,
        }));
      },
      error: err => {
        if (companyId !== this.companyId || vendor !== this.adapter.name) return;
        this.planMappingLoading = false;
        this.planMapping = null;
        this.planMappingRows = [];
        this.planMappingError = this.describeError(err, 'Failed to load plan mapping');
      },
    });
  }

  onTestCredentials(): void {
    if (!this.hasCredentialInput || this.isTestingCredentials) return;
    this.isTestingCredentials = true;
    this.credentialError = '';
    this.credentialTestResult = null;
    this.vendorSyncService
      .testCredentials(this.adapter.name, this.companyId, this.buildCredentialPayload())
      .subscribe({
        next: response => {
          this.isTestingCredentials = false;
          this.credentialTestResult = response.data ?? null;
        },
        error: err => {
          this.isTestingCredentials = false;
          this.credentialError = this.describeError(err, 'Could not verify these credentials');
        },
      });
  }

  onSaveCredentials(): void {
    if (!this.hasCredentialInput || this.isSavingCredentials || !this.canEditCredentials) return;
    this.isSavingCredentials = true;
    this.credentialError = '';
    this.vendorSyncService
      .saveCredentials(this.adapter.name, this.companyId, this.buildCredentialPayload())
      .subscribe({
        next: () => {
          this.isSavingCredentials = false;
          this.clearCredentialInputs();
          this.messageService.add({
            severity: 'success',
            summary: 'Connected',
            detail: `${this.label} credentials saved for this car wash.`,
          });
          // Saving credentials is what enrols this shop in the nightly sweep, so
          // other vendor-aware UI needs to re-read its cached flags.
          this.vendorModeService.refresh();
          this.loadCredentialStatus();
          this.loadPlanMapping();
        },
        error: err => {
          this.isSavingCredentials = false;
          this.credentialError = this.describeError(err, 'Failed to save credentials');
        },
      });
  }

  onToggleCredentialForm(): void {
    this.showCredentialForm = !this.showCredentialForm;
    this.credentialError = '';
    this.credentialTestResult = null;
    if (!this.showCredentialForm) this.clearCredentialInputs();
  }

  /**
   * Hold this vendor back from the nightly sweep, or put it back. Pause is left
   * alone, so Sync now keeps working — this is a canary's manual-only window.
   */
  onToggleNightlySync(): void {
    if (this.isTogglingNightlySync || this.isJobRunning || this.isPaused) return;
    const next = !this.isNightlySyncOn;
    this.isTogglingNightlySync = true;
    this.credentialError = '';
    this.vendorSyncService.setNightlySync(this.adapter.name, this.companyId, next).subscribe({
      next: response => {
        this.isTogglingNightlySync = false;
        // Render from the server's answer rather than assuming the write landed.
        this.credentialStatus = response.data ?? this.credentialStatus;
        this.messageService.add({
          severity: 'success',
          summary: next ? 'Nightly sync on' : 'Nightly sync off',
          detail: next
            ? `${this.label} will sync every night at 05:30 PT.`
            : `${this.label} will only sync when you click Sync now. Turn nightly back on once the canary passes.`,
        });
      },
      error: err => {
        this.isTogglingNightlySync = false;
        this.credentialError = this.describeError(err, 'Failed to update nightly sync');
      },
    });
  }

  /** Pause or resume. Non-destructive: credentials and the mapping both survive. */
  onToggleSyncEnabled(): void {
    if (this.isTogglingSyncEnabled || this.isJobRunning) return;
    const next = !this.credentialStatus?.enabled;
    this.isTogglingSyncEnabled = true;
    this.credentialError = '';
    this.vendorSyncService.setSyncEnabled(this.adapter.name, this.companyId, next).subscribe({
      next: response => {
        this.isTogglingSyncEnabled = false;
        // Render from the server's answer rather than assuming the write landed.
        this.credentialStatus = response.data ?? this.credentialStatus;
        this.vendorModeService.refresh();
        this.messageService.add({
          severity: 'success',
          summary: next ? 'Sync resumed' : 'Sync paused',
          detail: next
            ? `${this.label} will sync on the nightly schedule again.`
            : `${this.label} will not sync. Imported customers and their memberships are unaffected.`,
        });
      },
      error: err => {
        this.isTogglingSyncEnabled = false;
        this.credentialError = this.describeError(err, 'Failed to update sync state');
      },
    });
  }

  /**
   * Whether this vendor may run on the laptop agent, per the backend descriptor.
   * Never inferred from the vendor name here — the backend owns that list, and a
   * copy in the frontend would drift into offering a toggle the executor route
   * rejects with a 400.
   */
  get agentCapable(): boolean {
    return this.adapter.agentCapable === true;
  }

  get executor(): 'server' | 'agent' {
    return this.credentialStatus?.executor === 'agent' ? 'agent' : 'server';
  }

  /**
   * Which executor this vendor should be on, per the backend descriptor.
   * Defaults to 'server' when absent — both the safe answer and what a backend
   * predating the field assumed.
   */
  get recommendedExecutor(): 'server' | 'agent' {
    return this.adapter.recommendedExecutor === 'agent' ? 'agent' : 'server';
  }

  /** Whether the operator is currently on the executor the backend recommends. */
  get isOnRecommendedExecutor(): boolean {
    return this.executor === this.recommendedExecutor;
  }

  /** Agents holding a live SSE connection right now. */
  private get connectedAgents(): VendorAgentSummary[] {
    return this.agents.filter(a => a.connected);
  }

  /**
   * Can a connected agent actually run THIS vendor?
   *
   * Mirrors agentJobService.assertConnectedAgentCanRun exactly, and has to:
   * warning about a combination the server will happily accept trains operators
   * to ignore the warning, and staying silent about one it will refuse at 409
   * is how a misconfiguration reaches the 05:30 PT sweep unnoticed.
   *
   *   'none'      — nothing connected. The server still accepts (the job row is
   *                 durable and waits), so this is informational, not a block.
   *   'unknown'   — at least one connected agent predates capability reporting,
   *                 so nobody can tell. The server allows; say so plainly
   *                 rather than implying it is fine.
   *   'capable'   — some connected agent ships this adapter.
   *   'incapable' — agents are connected, all reported, none has this vendor.
   *                 This is the only state the server refuses.
   */
  get agentCapabilityState(): 'none' | 'unknown' | 'capable' | 'incapable' {
    const live = this.connectedAgents;
    if (!live.length) return 'none';
    if (live.some(a => !Array.isArray(a.supportedVendors))) return 'unknown';
    return live.some(a => (a.supportedVendors ?? []).includes(this.adapter.name))
      ? 'capable'
      : 'incapable';
  }

  /**
   * True when choosing Agent right now would be refused by the server.
   * Drives the inline warning, NOT a disabled button: the operator may be
   * about to start a laptop that does ship the adapter, and disabling the
   * control would leave them no way to pre-configure it.
   */
  get agentWouldBeRefused(): boolean {
    return this.agentCapabilityState === 'incapable';
  }

  /** Names of connected agents, for a message that says which machine to fix. */
  get connectedAgentNames(): string {
    return this.connectedAgents.map(a => a.name).join(', ');
  }

  get connectedAgentSummary(): string {
    const live = this.connectedAgents;
    if (!live.length) {
      const last = this.agents[0];
      return last?.lastSeenAt
        ? `No agent connected (last seen ${this.formatDate(last.lastSeenAt)})`
        : 'No agent connected';
    }
    return live.map(a => `${a.name}${a.agentVersion ? ` v${a.agentVersion}` : ''}`).join(', ');
  }

  loadAgents(): void {
    if (!this.agentCapable) {
      this.agents = [];
      return;
    }
    this.vendorSyncService.listAgents().subscribe({
      next: res => {
        this.agents = res.data?.agents ?? [];
      },
      error: () => {
        this.agents = [];
      },
    });
  }

  loadLatestAgentJob(): void {
    if (!this.agentCapable) {
      this.agentJob = null;
      return;
    }
    this.vendorSyncService.getLatestAgentJob(this.adapter.name, this.companyId).subscribe({
      next: res => {
        const job = res.data?.job ?? null;
        this.agentJob = job;
        // Pick a still-running delegated sync back up. The agent job row is
        // durable and outlives this component, so a reload or a tab change
        // mid-run must not leave the operator with no progress at all — before
        // this, reopening the panel showed nothing until the run finished.
        if (job && !TERMINAL_AGENT_STATUSES.has(job.status)) {
          this.followAgentJob(job.id);
        }
      },
      error: () => {
        this.agentJob = null;
      },
    });
  }

  onSetExecutor(executor: 'server' | 'agent'): void {
    if (this.isSavingExecutor || this.executor === executor) return;
    this.isSavingExecutor = true;
    this.executorError = '';
    this.vendorSyncService.setExecutor(this.adapter.name, this.companyId, executor).subscribe({
      next: () => {
        this.isSavingExecutor = false;
        if (this.credentialStatus) {
          this.credentialStatus = { ...this.credentialStatus, executor };
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Executor updated',
          detail:
            executor === 'agent'
              ? `${this.label} syncs will run on the agent machine.`
              : `${this.label} syncs will run on the cloud worker.`,
        });
        this.loadLatestAgentJob();
      },
      error: err => {
        this.isSavingExecutor = false;
        this.executorError = this.describeError(err, 'Failed to update executor');
      },
    });
  }

  onAddPlanMappingRow(): void {
    const name = this.newPlanName.trim();
    if (!name || !this.newPackageId) return;
    const existing = this.planMappingRows.findIndex(
      row => row.planName.toLowerCase() === name.toLowerCase()
    );
    if (existing >= 0) {
      this.planMappingRows[existing] = {
        planName: name,
        packageId: this.newPackageId,
        valid: true,
      };
    } else {
      this.planMappingRows = [
        ...this.planMappingRows,
        { planName: name, packageId: this.newPackageId, valid: true },
      ];
    }
    this.newPlanName = '';
    this.newPackageId = '';
    this.planMappingSaved = false;
  }

  onRemovePlanMappingRow(index: number): void {
    this.planMappingRows = this.planMappingRows.filter((_, i) => i !== index);
    this.planMappingSaved = false;
  }

  /**
   * Clear a row's stale invalid marker once it is repointed — the flag came from
   * the previous package and would otherwise keep warning about a fixed row
   * until the next fetch.
   */
  onPlanMappingRowChanged(row: PlanMappingRow): void {
    row.valid = true;
    row.reason = undefined;
    this.planMappingSaved = false;
  }

  onSavePlanMapping(): void {
    if (this.isSavingPlanMapping || !this.planMapping?.has_credentials) return;
    const map: Record<string, string> = {};
    this.planMappingRows.forEach(row => {
      if (row.planName.trim() && row.packageId) map[row.planName.trim()] = row.packageId;
    });
    this.isSavingPlanMapping = true;
    this.planMappingError = '';
    this.planMappingSaved = false;
    this.planMappingService.updatePlanMapping(this.companyId, this.adapter.name, map).subscribe({
      next: response => {
        this.isSavingPlanMapping = false;
        this.planMappingSaved = true;
        // Re-seed from the server: it re-validates every entry, so a package that
        // is inactive or belongs elsewhere comes back flagged.
        this.planMapping = response.data ?? this.planMapping;
        this.planMappingRows = (this.planMapping?.plan_package_map ?? []).map(entry => ({
          planName: entry.plan_name,
          packageId: entry.package_id,
          valid: entry.valid,
          reason: entry.reason,
        }));
      },
      error: err => {
        this.isSavingPlanMapping = false;
        this.planMappingError = this.describeError(err, 'Failed to save plan mapping');
      },
    });
  }

  onStartSync(): void {
    if (!this.canStartSync) return;
    this.isStartingSync = true;
    this.jobFailedReason = null;
    const { limit } = parseSyncLimit(this.syncLimit, this.syncLimitBadInput);
    this.vendorSyncService.triggerSync(this.adapter.name, this.companyId, { limit }).subscribe({
      next: response => {
        this.isStartingSync = false;
        const jobId = response.data?.jobId ?? null;
        if (!jobId) return;
        this.jobId = jobId;
        this.runningSyncLimit = limit;
        this.jobState = 'waiting';
        this.jobProgress = null;
        // Drop the previous run's agent job so a stale `completed` cannot show
        // under the sync that has just started.
        this.agentJobId = null;
        this.agentJob = null;
        this.startPolling(jobId);
      },
      error: err => {
        this.isStartingSync = false;
        this.credentialError = this.describeError(err, 'Could not start the sync');
      },
    });
  }

  onOpenCutOver(): void {
    this.showCutOver = true;
    this.cutOverConfirmation = '';
  }

  onCancelCutOver(): void {
    this.showCutOver = false;
    this.cutOverConfirmation = '';
  }

  /**
   * Finish the migration: Way becomes the only source of truth.
   *
   * This is the existing credential delete. Deleting the row is what withdraws
   * the shop from the nightly sweep (syncScheduler derives its targets from the
   * credential table), and it takes the stored plan mapping's usefulness with
   * it — but imported customers, memberships and vehicles are untouched, which
   * is why this is safe to present as the intended ending rather than a
   * destructive last resort.
   */
  onConfirmCutOver(): void {
    if (!this.cutOverConfirmed || this.isCuttingOver) return;
    this.isCuttingOver = true;
    this.credentialError = '';
    this.vendorSyncService.deleteCredentials(this.adapter.name, this.companyId).subscribe({
      next: () => {
        this.isCuttingOver = false;
        this.showCutOver = false;
        this.vendorModeService.refresh();
        this.messageService.add({
          severity: 'success',
          summary: `Cut over from ${this.label}`,
          detail:
            'The nightly sync has stopped and the stored credentials are deleted. Imported customers and memberships are unaffected.',
        });
        this.back.emit();
      },
      error: err => {
        this.isCuttingOver = false;
        this.credentialError = this.describeError(err, 'Failed to cut over');
      },
    });
  }

  onBack(): void {
    this.back.emit();
  }

  trackByKey(_index: number, field: { key: string }): string {
    return field.key;
  }

  /**
   * Follow the BullMQ job, handing over to the agent job if it delegates.
   *
   * An agent-executed sync's BullMQ job is done within a second of the click:
   * the worker writes `{ delegated, agentJobId }`, returns, and releases its
   * single slot rather than holding it for the hour the laptop takes. So
   * polling only this job reported the sync `completed` — with zero counts —
   * before the agent had fetched a row. The delegation marker is the handover
   * signal; from there the durable agent job row is the only progress there is.
   */
  private startPolling(jobId: string): void {
    this.stopPolling();
    this.pollSub = timer(0, JOB_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.vendorSyncService.getSyncJob(jobId).pipe(catchError(() => of(null)))),
        takeWhile(response => {
          // Stop on delegation as well as on a terminal state: the handover
          // below replaces this stream, and continuing to read a job that is
          // finished-but-not-really would overwrite the agent's counts.
          if (response?.data?.progress?.delegated) return false;
          const state = response?.data?.state ?? null;
          // Keep the final tick so terminal counts are rendered, then stop.
          return !state || !TERMINAL_STATES.has(state);
        }, true)
      )
      .subscribe(response => {
        const data = response?.data;
        if (!data) return;

        if (data.progress?.delegated) {
          const agentJobId = data.progress.agentJobId ?? null;
          if (agentJobId) {
            this.followAgentJob(agentJobId);
          } else {
            // Delegated, but the worker's progress write did not carry the id.
            // Ask the server which job belongs to this shop rather than
            // reporting a sync that never ran as complete.
            this.loadLatestAgentJob();
          }
          return;
        }

        this.jobState = data.state ?? this.jobState;
        this.jobProgress = data.progress ?? this.jobProgress;
        this.jobFailedReason = data.failedReason ?? null;
      });
  }

  /**
   * Poll one agent job until it stops, rendering it through the same panel.
   *
   * The agent's `counts` payload uses the field names the server path
   * publishes, so it assigns straight onto `jobProgress` and every derived
   * getter keeps working. Its heartbeat is 30s by default
   * (`VENDOR_AGENT_PROGRESS_MS`) and each uploaded part publishes too, so this
   * poll is faster than the data it reads — which is the right way round.
   */
  private followAgentJob(agentJobId: string): void {
    if (this.agentJobId === agentJobId && this.agentPollSub) return;

    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.agentPollSub?.unsubscribe();
    this.agentJobId = agentJobId;
    this.jobFailedReason = null;
    // Refresh the roster so the "no agent connected" hint below is current
    // rather than whatever was true when the panel first opened.
    this.loadAgents();

    this.agentPollSub = timer(0, JOB_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.vendorSyncService.getAgentJob(agentJobId).pipe(catchError(() => of(null)))
        ),
        takeWhile(response => {
          const status = response?.data?.status ?? null;
          return !status || !TERMINAL_AGENT_STATUSES.has(status);
        }, true)
      )
      .subscribe(response => {
        const job = response?.data;
        if (!job) return;
        this.agentJob = job;
        this.jobState = AGENT_STATUS_TO_JOB_STATE[job.status] ?? job.status;
        this.jobProgress = job.counts ?? this.jobProgress;
        this.jobFailedReason = job.errorMessage ?? null;
      });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.agentPollSub?.unsubscribe();
    this.agentPollSub = null;
  }

  private buildCredentialPayload(): VendorCredentialInput {
    const payload: VendorCredentialInput = {};
    this.credentialFields.forEach(field => {
      const value = (this.credentialValues[field.key] ?? '').trim();
      if (value) payload[field.key] = value;
    });
    return payload;
  }

  /** Never leave a password sitting in component state after it has been used. */
  private clearCredentialInputs(): void {
    this.credentialValues = {};
    this.credentialTestResult = null;
  }

  private resetForAdapterChange(): void {
    this.credentialStatus = null;
    this.planMapping = null;
    this.planMappingRows = [];
    this.clearCredentialInputs();
    this.stopPolling();
    this.plateMatchingError = '';
    this.jobId = null;
    this.jobState = null;
    this.jobProgress = null;
    this.agentJobId = null;
    this.agentJob = null;
    this.syncLimit = null;
    this.syncLimitBadInput = false;
    this.runningSyncLimit = null;
    this.showCutOver = false;
  }

  private describeError(err: unknown, fallback: string): string {
    const response = err as { error?: { message?: string }; message?: string; status?: number };
    if (response?.status === 0) {
      return `${fallback}: the vendor sync service could not be reached.`;
    }
    return response?.error?.message || response?.message || fallback;
  }
}
